import { useCallback, useMemo } from 'react';

import { useEvent } from 'react-use';
import {
  markDocumentStatusJobCancelling,
  writeDocumentStatusJobToCache,
} from 'src/components/reports/report-detail/document-status-job-cache';
import type { DocumentStatusJobProgress } from 'src/components/reports/report-detail/document-status-jobs';
import {
  getDocumentStatusJobProgress,
  getIsDocumentStatusJobActive,
} from 'src/components/reports/report-detail/document-status-jobs';
import { patchSetDocumentsStatusCache } from 'src/components/reports/report-detail/grid-document-status';
import { Apollo } from 'src/gql/generated';
import { Sentry } from 'src/sentry';

export interface DocumentStatusJobView {
  id: string;
  progress: DocumentStatusJobProgress;
}

/** Always on, so every tab patches the grid and tracks jobs the user starts anywhere. */
export function useSubscribeToDocumentStatusJobs(): void {
  Apollo.useOnDocumentStatusJobProgressSubscription({
    ignoreResults: true,
    onData: ({ client, data: subscriptionData }) => {
      const event = subscriptionData.data?.documentStatusJobProgress;
      if (!event) return;

      patchSetDocumentsStatusCache(client.cache, { ...event, status: event.job.status });
      writeDocumentStatusJobToCache(client.cache, event.job);
    },
    onError: error => {
      Sentry.captureException(error, { extra: { subscription: 'DocumentStatusJobProgress' } });
    },
    shouldResubscribe: true,
  });
}

/**
 * The user's background status jobs with their rolled-up progress. Coming back
 * to the tab while a job is active refetches, in case its last event was missed.
 */
export function useDocumentStatusJobs(): { jobs: DocumentStatusJobView[]; loading: boolean } {
  const { data, loading, refetch } = Apollo.useGetDocumentStatusJobsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const jobs = useMemo(
    () =>
      (data?.documentStatusJobs ?? []).map(job => ({
        id: job.id,
        progress: getDocumentStatusJobProgress(job),
      })),
    [data]
  );
  const hasActiveJob = jobs.some(job => getIsDocumentStatusJobActive(job.progress.phase));
  const refetchWhenVisible = useCallback(() => {
    if (hasActiveJob && document.visibilityState === 'visible') void refetch();
  }, [hasActiveJob, refetch]);
  useEvent('visibilitychange', refetchWhenVisible, document);

  return { jobs, loading };
}

export function useCancelDocumentStatusJob(): {
  cancelJob: (jobId: string) => Promise<void>;
  loading: boolean;
} {
  const [cancelDocumentStatusJob, { loading }] = Apollo.useCancelDocumentStatusJobMutation();
  const cancelJob = useCallback(
    async (jobId: string) => {
      await cancelDocumentStatusJob({
        // A job that had already finished gets its final state from the refetch.
        refetchQueries: [Apollo.GetDocumentStatusJobsDocument],
        update: cache => markDocumentStatusJobCancelling(cache, jobId),
        variables: { jobId },
      });
    },
    [cancelDocumentStatusJob]
  );

  return { cancelJob, loading };
}
